import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { createLookup, fetchForecastDrivers, updateLookup } from '@/api/client'
import { ApiError } from '@/api/client'
import type { LookupValue } from '@/api/types'

import { ListTree } from 'lucide-react'
import { AdminSection } from '@/features/admin/AdminSection'

const LIST_NAME = 'forecast_driver'

export function DriversAdminPage() {
  const qc = useQueryClient()
  const driversQuery = useQuery({
    queryKey: ['config', 'lookups', LIST_NAME],
    queryFn: fetchForecastDrivers,
  })
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState({ code: '', label: '', sort_order: 10 })

  const createMut = useMutation({
    mutationFn: createLookup,
    onSuccess: async () => {
      setError(null)
      setDraft({ code: '', label: '', sort_order: 10 })
      await qc.invalidateQueries({ queryKey: ['config', 'lookups'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create driver')
    },
  })

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof updateLookup>[1] }) =>
      updateLookup(id, body),
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['config', 'lookups'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to update driver')
    },
  })

  const drivers = driversQuery.data ?? []

  return (
    <AdminSection
      title="Forecast drivers"
      tone="teal"
      icon={ListTree}
      lede="Maintain the lookup list used when attributing forecast changes on opportunities."
    >
      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      <div className="table-wrap">
        <table className="table table--dense">
          <thead>
            <tr>
              <th>Label</th>
              <th>Code</th>
              <th>Sort</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {drivers.map((driver) => (
              <DriverRow
                key={driver.id}
                driver={driver}
                busy={patchMut.isPending}
                onSave={(body) => patchMut.mutate({ id: driver.id, body })}
              />
            ))}
          </tbody>
        </table>
      </div>

      <form
        className="admin-form"
        onSubmit={(event) => {
          event.preventDefault()
          createMut.mutate({
            list_name: LIST_NAME,
            code: draft.code.trim(),
            label: draft.label.trim(),
            sort_order: Number(draft.sort_order),
          })
        }}
      >
        <h3 className="admin-form__title">Add driver</h3>
        <div className="admin-form__row">
          <label className="field">
            <span className="field__label">Code</span>
            <input
              className="field__input"
              value={draft.code}
              onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
              required
            />
          </label>
          <label className="field">
            <span className="field__label">Label</span>
            <input
              className="field__input"
              value={draft.label}
              onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
              required
            />
          </label>
          <label className="field">
            <span className="field__label">Sort</span>
            <input
              className="field__input"
              type="number"
              value={draft.sort_order}
              onChange={(e) => setDraft((d) => ({ ...d, sort_order: Number(e.target.value) }))}
            />
          </label>
          <button className="button button--primary" type="submit" disabled={createMut.isPending}>
            Add
          </button>
        </div>
      </form>
    </AdminSection>
  )
}

function DriverRow({
  driver,
  busy,
  onSave,
}: {
  driver: LookupValue
  busy: boolean
  onSave: (body: Parameters<typeof updateLookup>[1]) => void
}) {
  const [label, setLabel] = useState(driver.label)
  const [sortOrder, setSortOrder] = useState(driver.sort_order ?? 0)

  return (
    <tr>
      <td>
        <input
          className="field__input field__input--compact"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </td>
      <td>
        <code>{driver.code}</code>
      </td>
      <td>
        <input
          className="field__input field__input--compact"
          type="number"
          value={sortOrder}
          onChange={(e) => setSortOrder(Number(e.target.value))}
        />
      </td>
      <td className="table__actions">
        <button
          type="button"
          className="button button--ghost"
          disabled={busy}
          onClick={() => onSave({ label, sort_order: sortOrder })}
        >
          Save
        </button>
      </td>
    </tr>
  )
}
